import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { GraduationCap, BookOpen, X } from 'lucide-react';
import { toast } from '../hooks/use-toast';

/**
 * RoleSelector — shown once when a logged-in user has no role yet.
 * User picks Student or Teacher; admins bypass this via email allowlist.
 */
export default function RoleSelector() {
  const { user, setRole } = useAuth();
  const [busy, setBusy] = useState(false);
  const dismissKey = user ? `role_selector_dismissed_${user.user_id}` : null;
  const [dismissed, setDismissed] = useState(() => !!(dismissKey && localStorage.getItem(dismissKey)));

  const dismiss = () => { if (dismissKey) localStorage.setItem(dismissKey, '1'); setDismissed(true); };

  if (!user || user.role || dismissed) return null;

  const pick = async (role) => {
    try {
      setBusy(true);
      await setRole(role);
      toast({ title: `${role === 'teacher' ? 'ଶିକ୍ଷକ' : 'ଛାତ୍ର'} role selected ✅` });
      dismiss();
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'Try again' });
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" data-testid="role-selector-overlay">
      <div className="w-full max-w-md bg-white rounded-3xl p-5 shadow-2xl animate-in slide-in-from-bottom">
        <div className="flex justify-between items-start mb-3">
          <div>
            <div className="text-lg font-extrabold text-gray-900">👋 ସ୍ୱାଗତ, {user.name?.split(' ')[0] || 'ବନ୍ଧୁ'}!</div>
            <p className="text-xs text-gray-600 mt-1">ଆପଣ କିଏ? ଆପଣଙ୍କ ପାଇଁ App personalize କରିବି</p>
          </div>
          <button onClick={dismiss} className="text-gray-400 hover:text-gray-600" data-testid="role-selector-close">
            <X size={18} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4">
          <button
            onClick={() => pick('student')}
            disabled={busy}
            data-testid="role-select-student"
            className="p-4 rounded-2xl border-2 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 hover:border-emerald-400 transition-all disabled:opacity-50 text-left"
          >
            <div className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center mb-2">
              <GraduationCap size={22} className="text-white" />
            </div>
            <div className="font-extrabold text-sm text-emerald-800">ଛାତ୍ର</div>
            <div className="text-[11px] text-emerald-700 mt-1">Student · Quiz, notes, AI doubt chat</div>
          </button>

          <button
            onClick={() => pick('teacher')}
            disabled={busy}
            data-testid="role-select-teacher"
            className="p-4 rounded-2xl border-2 border-purple-200 bg-purple-50 hover:bg-purple-100 hover:border-purple-400 transition-all disabled:opacity-50 text-left"
          >
            <div className="w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center mb-2">
              <BookOpen size={22} className="text-white" />
            </div>
            <div className="font-extrabold text-sm text-purple-800">ଶିକ୍ଷକ</div>
            <div className="text-[11px] text-purple-700 mt-1">Teacher · AI Lesson Plans, Resources</div>
          </button>
        </div>

        <p className="text-[10px] text-gray-500 mt-4 text-center">Later profile ରୁ change କରି ପାରିବେ</p>
      </div>
    </div>
  );
}
